<template>
  <hs-modal v-model="open" title="Create New Group" size="md" @close="$emit('close')">
    <form id="cgm-form" class="cgm-form" @submit.prevent="handleSubmit">
      <p class="cgm-desc">
        Create a group to collaborate with other stores. Share products, manage inventory transfers, and streamline operations.
      </p>

      <hs-input
        v-model="form.name"
        label="Group Name"
        placeholder="e.g., Downtown Partners"
        required
        :hint="`${form.name.length}/120 characters`"
        :error="nameError"
      />

      <hs-textarea
        v-model="form.description"
        label="Description"
        placeholder="Describe the purpose of this group…"
        :rows="3"
        hint="Optional"
      />

      <div class="cgm-store-box">
        <hs-checkbox
          v-model="form.addCurrentStore"
          label="Add this store to the group"
          :description="`Store #${storeId} will be automatically added as a member`"
        />
      </div>

      <hs-alert v-if="error" tone="danger" :message="error" />
    </form>

    <template #footer>
      <div class="cgm-footer">
        <hs-button variant="secondary" type="button" @click="$emit('close')">Cancel</hs-button>
        <hs-button
          variant="primary"
          icon="plus"
          type="submit"
          form="cgm-form"
          :loading="loading"
          :disabled="loading || !form.name.trim()"
        >
          Create Group
        </hs-button>
      </div>
    </template>
  </hs-modal>
</template>

<script>
import { ref } from "vue";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import { HsModal, HsInput, HsTextarea, HsCheckbox, HsButton, HsAlert } from "@/components/ui";

export default {
  name: "CreateGroupModal",
  components: { HsModal, HsInput, HsTextarea, HsCheckbox, HsButton, HsAlert },
  props: {
    storeId: { type: Number, required: true },
  },
  emits: ["close", "created"],
  setup(props, { emit }) {
    const open = ref(true);
    const loading = ref(false);
    const error = ref("");
    const nameError = ref("");
    const form = ref({ name: "", description: "", addCurrentStore: true });

    const handleSubmit = async () => {
      nameError.value = "";
      const name = form.value.name.trim();
      if (!name) { nameError.value = "Group name is required."; return; }
      if (name.length > 120) { nameError.value = "Name must be 120 characters or fewer."; return; }

      loading.value = true;
      error.value = "";

      try {
        const createResponse = await http.post("/v2/groups", {
          name,
          description: form.value.description.trim() || null,
        });

        const group = createResponse.data;
        toast.success(`Group "${name}" created successfully!`);

        if (form.value.addCurrentStore && group.id) {
          try {
            await http.post(`/v2/groups/${group.id}/join`, { store_id: props.storeId });
            toast.success(`Store #${props.storeId} added to the group`);
          } catch (joinError) {
            console.error("Failed to add store to group", joinError);
            toast.warning("Group created but failed to add store. You can join manually.");
          }
        }

        emit("created", group);
        emit("close");
      } catch (err) {
        console.error("Failed to create group", err);
        error.value = err?.response?.data?.error?.message || "Unable to create group. Please try again.";
      } finally {
        loading.value = false;
      }
    };

    return { open, loading, error, nameError, form, handleSubmit };
  },
};
</script>

<style scoped>
.cgm-form {
  display: grid;
  gap: 1.1rem;
}

.cgm-desc {
  color: var(--hs-muted);
  font-size: 0.83rem;
  line-height: 1.55;
  margin: 0;
}

.cgm-store-box {
  background: color-mix(in srgb, var(--hs-primary) 6%, transparent);
  border: 1px solid color-mix(in srgb, var(--hs-primary) 22%, transparent);
  border-radius: 12px;
  padding: 1rem 1.1rem;
}

.cgm-footer {
  display: flex;
  gap: 0.65rem;
  justify-content: flex-end;
}
</style>
